/* eslint-disable max-len */
import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Typography from '@digital-hig/mui/dist/Typography';
/* eslint-disable import/no-extraneous-dependencies */
import {
  Button, Tooltip, Link as TextLink, CardContent, CardActions, CardActionArea, SvgIcon,
} from '@mui/material';

import { Link } from 'react-router-dom';
import Card, { CardDivider } from '@digital-hig/mui/dist/Card';
// eslint-disable-next-line import/no-extraneous-dependencies
import Skeleton from 'react-loading-skeleton';
// eslint-disable-next-line import/no-extraneous-dependencies
import SVGUndo from '@digital-hig/icon/lib/esm/icons/undo';
import TrashIcon from '@digital-hig/icon/lib/esm/icons/trash';
import Waffle from '@digital-hig/icon/lib/esm/icons/more-vertical';
import Visible from '@digital-hig/icon/lib/esm/icons/visible';
import IconButton from '@digital-hig/mui/dist/IconButton';
import { getChildProperty } from '../common/object';
import {
  ENVIRONMENT,
  LEARNING_MODE_COURSEWARE,
  CATALOG_CARD_LOADING,
  styleVariables,
  CATALOG_CARD_LIST_EDIT,
  CATALOG_CARD_ROW,
  CATALOG_CARD_LIST_DELETED,
  MFE_CARD,
} from '../common/constants';
import ListCTA from './listCTA';
import { calculateEffortDisplay } from '../common/utilities';
import EnvContext from '../common/envContext';
import { generateCourseDataAnalyticsString, triggerManualLinkClick, triggerLearningPlanContentClick } from '../common/analyticsApi';
import {
  isLearnLiveClass,
  getContentItemStatus,
  getStatusDisplayText,
  CONTENT_ITEM_STATUS,
  isFastTrackLearningPlan,
} from '../common/fastTrackUtils';
import LearnLiveClassCard from './learnLiveClassCard';
import NumberedCircle from './numberedCircle';
import Chip from '../common/chip';

// Styled CardContent with completed state styling
const StyledCardContent = styled(CardContent)`
  &.completed {
    background-color: #f9f9f9;
    border: 1px solid #e5e5e5;
  }
`;

const CommonCard = ({
  data, learningRecords, className, clickEvent, setReturnUrl, analyticsArea, cardRefs, state, toggleDelete, deletedListItems, isThemeDark, canDrag, itemIndex, showNumberedCircle, isItemFromFastTrackLearningPlan,
  // moveCard,
}) => {
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  let displayType = '';
  let uri = '';
  let isCourse = false;
  let isCollection = false;
  let statusMessage;
  const isLoading = state === CATALOG_CARD_LOADING;

  // Memoize expensive calculations at the top level to avoid conditional hook calls
  const refData = useMemo(() => {
    const reformatData = (obj) => {
      let retData = { ...obj };
      if (!retData?.products && retData?.meta?.products) {
        retData.products = retData.meta.products;
      }
      if (retData?.tutorials) { // Curated Service
        retData.noOfTutorials = retData.tutorials.length;
      }
      if (retData?.relatedTutorials) { // Editorial Content Service and Search Service
        retData.noOfTutorials = retData.relatedTutorials.length;
      }
      if (retData?.totalTutorials) { // Related Learning Service
        retData.noOfTutorials = retData.totalTutorials;
      }
      if (retData.meta && retData?.meta?.effort?.displayName) {
        retData = { ...retData, ...retData.meta, effort: retData.meta.effort.displayName };
      }

      if (getChildProperty(data, 'relatedCurateds[0].slug')) {
        retData.parentSlug = getChildProperty(data, 'relatedCurateds[0].slug');
      }

      if (getChildProperty(data, 'linkedFrom.courseCollection.items[0].slug')) {
        retData.parentSlug = getChildProperty(data, 'linkedFrom.courseCollection.items[0].slug');
      }

      if (retData.curatedSlug) {
        retData.parentSlug = retData.curatedSlug;
      }

      // eslint-disable-next-line no-restricted-globals
      retData.effort = !isNaN(retData.effort) ? calculateEffortDisplay(retData.effort) : retData.effort;
      return retData;
    };
    return reformatData(data);
  }, [data]);

  // Memoize status calculations
  const status = useMemo(() => getContentItemStatus(refData, learningRecords, env),
    [refData, learningRecords, env]);

  const statusDisplayText = useMemo(() => getStatusDisplayText(status),
    [status]);

  const isCompleted = useMemo(() => status === CONTENT_ITEM_STATUS.COMPLETED,
    [status]);

  // Memoize displayEffort calculation - this will be overridden by specific content types
  let displayEffort = useMemo(() => {
    if (refData?.effort) {
      return <span>{refData.effort}</span>;
    }
    return undefined;
  }, [refData?.effort]);

  if (isLoading) {
    return (
      <RootElement>
        <Card className="padded">
          <StyledCardContent className="content">
            <Typography variant="short-copy-small" component="div">
              <Skeleton />
            </Typography>
            <Typography variant="headline-small" component="div" className="titleLink">
              <Skeleton />
            </Typography>
            <Typography variant="short-copy-small" component="div"><Skeleton /></Typography>
          </StyledCardContent>
          <div className="bottomSection">
            <CardDivider />
            <CardActions className="cardActions">
              <Skeleton />
            </CardActions>
          </div>
        </Card>
      </RootElement>
    );
  }

  // Check if this is a LearnLiveClass and use specialized card
  const earlyContentType = data?.objectType || data?.type;
  let earlyContentTypeStr = '';
  if (earlyContentType && typeof earlyContentType === 'string') {
    earlyContentTypeStr = earlyContentType.toLowerCase().replace(' ', '');
  } else if (Array.isArray(earlyContentType) && earlyContentType.length > 0) {
    earlyContentTypeStr = earlyContentType[0].toLowerCase().replace(' ', '');
  }

  if (earlyContentTypeStr === 'learnliveclass' && isLearnLiveClass(data)) {
    return (
      <LearnLiveClassCard
        data={data}
        learningRecords={learningRecords}
        analyticsArea={analyticsArea}
        clickEvent={clickEvent}
        env={env}
        itemIndex={itemIndex}
        isItemFromFastTrackLearningPlan={isItemFromFastTrackLearningPlan}
      />
    );
  }

  // Create status chip component
  if (status !== CONTENT_ITEM_STATUS.NOT_STARTED) {
    statusMessage = (
      <Chip
        value={{
          id: status,
          label: statusDisplayText,
        }}
      />
    );
  }

  let canSave = true;

  const displayTitle = !refData.title || refData?.title?.length < 90
    ? refData.title
    : `${refData.title.substring(0, refData.title.substring(0, 90).lastIndexOf(' '))}...`;

  const allProducts = refData?.products?.filter((p) => p?.guid !== '00000000-0000-0000-0000-000000000000').map((p) => p?.displayName).join(', ') || '';
  const displayProducts = allProducts.length < 60
    ? allProducts
    : `${allProducts.substring(0, allProducts.substring(0, 60).lastIndexOf(','))}...`;

  // Check for Fast Track content types using the 'objectType' field for routing logic
  // Fall back to 'type' field for backward compatibility
  const contentType = refData?.objectType || refData?.type;

  // Ensure contentType is a string before calling toLowerCase
  const contentTypeStr = contentType && typeof contentType === 'string' ? contentType.toLowerCase().replace(' ', '') : '';

  switch (contentTypeStr) {
  case 'module':
    if (!refData.isStandAlone) {
      canSave = false;
    }
    if (refData.learningMode === LEARNING_MODE_COURSEWARE) {
      displayType = <span>Module</span>;
      if (!refData.isStandAlone || refData.parentSlug) {
        uri = `/ondemand/course/${refData.parentSlug}/module/${refData?.id}`;
      } else {
        uri = `/ondemand/module/${refData?.slug}`;
      }
    } else {
      displayType = <span>Tutorial</span>;
      if (!refData.isStandAlone || refData.parentSlug) {
        uri = `/ondemand/curated/${refData.parentSlug}/${refData?.id}`;
      } else {
        uri = `/ondemand/tutorial/${refData?.slug}`;
      }
    }
    break;

  case 'course':
    isCourse = true;
    if (refData.learningMode === LEARNING_MODE_COURSEWARE) {
      displayType = <span>Course</span>;
      uri = `/ondemand/course/${refData?.slug}`;
    } else {
      displayType = <span>Curated List</span>;
      displayEffort = refData.noOfTutorials ? (
        <span>
          {refData.noOfTutorials}
          {' '}
          tutorials
        </span>
      ) : undefined;
      uri = `/ondemand/curated/${refData?.slug}`;
    }
    break;
  case 'learningpathway':
    isCollection = true;
    displayType = <span>Collection</span>;
    uri = refData.uri || `/ondemand/collection/${refData?.slug}`;
    displayEffort = refData.itemCount ? (
      <span>
        {refData.itemCount}
        {' '}
        items
      </span>
    ) : undefined;
    break;

  default:
    break;
  }

  // Override URI with URL from MFE service
  if (refData.url) {
    uri = refData.url;
  }

  if (state === CATALOG_CARD_ROW || state === CATALOG_CARD_LIST_EDIT || state === CATALOG_CARD_LIST_DELETED) {
    const card = (
      <RootElement className={deletedListItems.includes(data.id) && 'deleted'} isThemeDark={isThemeDark}>
        <Card className="padded cardRow">
          <StyledCardContent className={`shortContent ${isCompleted && isFastTrackLearningPlan(data) ? 'completed' : ''}`} id={data.id}>
            <div className={state === CATALOG_CARD_LIST_EDIT ? 'list-edit-header' : ''}>
              {(state === CATALOG_CARD_LIST_EDIT && !deletedListItems.includes(data.id)) && canDrag && (
                <div className="waffle-icon">
                  <SvgIcon size="medium">
                    <Waffle />
                  </SvgIcon>
                  <SvgIcon size="medium">
                    <Waffle />
                  </SvgIcon>
                </div>
              )}
              {showNumberedCircle && (
                <NumberedCircle
                  number={itemIndex + 1}
                  isCompleted={isCompleted}
                  sx={{ marginRight: '1rem' }}
                />
              )}
              <div className="shortTop">
                <Typography variant="headline-smaller" component="div" className="titleLink">
                  <TextLink
                    component={Link}
                    to={uri}
                    onClick={() => {
                      clickEvent({
                        href: uri, data: refData, analyticsArea, buttonName: 'title', learningRecords, env, isItemFromFastTrackLearningPlan,
                      }); setReturnUrl({ data: refData });
                    }}
                    key={refData.id}
                  >
                    {refData?.title}
                  </TextLink>
                </Typography>
                <Typography variant="short-copy-small" component="div" color={!isThemeDark ? 'ink-on-background-60' : null} className="cardType">
                  {statusMessage}
                  {displayType}
                  {displayEffort}
                </Typography>
              </div>
            </div>
            <div className={state === CATALOG_CARD_LIST_EDIT ? 'list-edit-actions' : ''}>
              {!deletedListItems.includes(data.id) && canSave && state === CATALOG_CARD_LIST_EDIT && <ListCTA data={data} isCourse={isCourse} isCollection={isCollection} analyticsArea={analyticsArea} />}
              {!deletedListItems.includes(data.id) && state === CATALOG_CARD_LIST_EDIT && toggleDelete && (
                <div className="editButtons">
                  <span className="border" />
                  <IconButton
                    variant="text"
                    data-testid="delete-icon-common-card"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDelete({ id: data.id });
                      triggerManualLinkClick({
                        linkTitle: `${analyticsArea}:delete:${generateCourseDataAnalyticsString({
                          data, learningRecords, env,
                        })}`,
                      });
                    }}
                  >
                    <SvgIcon id="listItemDelete" size="large"><TrashIcon /></SvgIcon>
                  </IconButton>
                </div>
              )}
            </div>
            {deletedListItems.includes(data.id) && toggleDelete && <Button id="listItemDelete" startIcon={<SvgIcon><SVGUndo /></SvgIcon>} variant="text" className="undoDelete" onClick={() => { toggleDelete({ id: data.id }); }}>Undo delete</Button> }
          </StyledCardContent>
        </Card>
      </RootElement>
    );

    return <div>{card}</div>;
  }

  const cardContent = (
    <StyledCardContent className={`content ${isCompleted && isFastTrackLearningPlan(data) ? 'completed' : ''}`}>
      <div className="cardTop">
        {showNumberedCircle && (
          <NumberedCircle
            number={itemIndex + 1}
            isCompleted={isCompleted}
          />
        )}
        {statusMessage}
        <Typography variant="short-copy-small" component="div" color={!isThemeDark ? 'ink-on-background-60' : null} className="cardType">
          {displayType}
          {displayEffort}
        </Typography>
      </div>
      {displayTitle === refData?.title ? ( // Not trucated title
        <Typography variant="headline-small" component="div" className="titleLink">
          {displayTitle}
        </Typography>
      ) : ( // Truncated title
        <Tooltip
          title={refData?.title}
          arrow
          enterTouchDelay={0}
        >
          <Typography variant="headline-small" component="div" className="titleLink">
            {displayTitle}
          </Typography>
        </Tooltip>
      )}

      {allProducts === displayProducts ? (
        <Typography variant="short-copy-small" component="div">{displayProducts}</Typography>
      )
        : (
          <Tooltip
            title={allProducts}
            arrow
            enterTouchDelay={0}
          >
            <Typography variant="short-copy-small" component="div">{displayProducts}</Typography>
          </Tooltip>
        )}
    </StyledCardContent>
  );

  if (state === MFE_CARD) {
    return (

      <RootElement className="mfeCard cardRow">
        <CardActionArea
          href={uri}
          target="_blank"
          onClick={() => {
            clickEvent({
              href: uri, data: refData, analyticsArea, buttonName: 'card', learningRecords, env, isItemFromFastTrackLearningPlan,
            });
          }}
        >
          <Card className="padded">
            {cardContent}
            <CardActions style={{

            }}
            >
              <Button
                variant="link-button"
                href={uri}
                target="_blank"
                startIcon={(
                  <SvgIcon>
                    <Visible />
                  </SvgIcon>
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  clickEvent({
                    href: uri, data: refData, analyticsArea, buttonName: 'view', learningRecords, env, isItemFromFastTrackLearningPlan,
                  });
                }}
              >
                View
              </Button>
            </CardActions>
          </Card>
        </CardActionArea>
      </RootElement>

    );
  }

  return (

    // eslint-disable-next-line no-param-reassign,no-return-assign
    <RootElement className={`cardWrapper ${className}`} ref={(element) => cardRefs.current[`card${refData.id}`] = element}>
      <Card className="padded">
        {cardContent}

        <div className="bottomSection">
          <CardDivider />
          <CardActions className="cardActions">
            <Button
              component={Link}
              to={uri}
              variant="outlined"
              size="small"
              onClick={() => {
                clickEvent({
                  href: uri, data: refData, analyticsArea, buttonName: 'view', learningRecords, env,
                }); setReturnUrl({ data: refData });
              }}
            >
              View
            </Button>

            {canSave && <ListCTA data={data} isCourse={isCourse} isCollection={isCollection} analyticsArea={analyticsArea} />}
          </CardActions>
        </div>

      </Card>
    </RootElement>

  );
};

const RootElement = styled.span`

  display: flex;
  width: 100%;
  
  &.mfeCard {
    flex-direction: column;
    padding-bottom: 3px;

    a[class*="MuiCardActionArea-root"] {
      display: flex;
      width: 100%;
      flex: 1;
      align-items: stretch;     
    }

    div[class*="MuiCardActions-root"] {
      flex-direction: column-reverse;
      padding: 36px 12px 14px;
    }
  }

  &.cardWrapper {
    margin: 3px 0;
  }

  &.deleted .cardRow .shortTop {
    opacity: 0.3;
  }

  .cardRow {
    border-radius: 0px !important;
    margin-bottom: -1px;

     .editButtons {
       display: flex;
       align-items: start;
       .border {
         height: 30px;
         border-left: 1px solid ${styleVariables.lightDivider};
         margin: 5px 12px;
       }
     }
     .waffle-icon{
      margin-right: 10px;
      > svg:first-child{
        margin-right: -10px;
      }
     }
    .shortContent {
      display: flex;
      width: 100%;
      padding:20px 24px 20px 20px;
      justify-content: space-between;
      .undoDelete {
        height: fit-content;
      }

      .shortTop {
        flex: 1;
      }

      .cardType {
        display: flex;

        > span {
          align-content: center;
          display: flex;
          flex-wrap: wrap;
        }

        > *:not(:first-child) {
          margin: 5px 0px 5px 10px;
          padding-left: 10px;
          border-left: 1px solid ${styleVariables.lightDivider};
          line-height: 12px;
        }
      }

      .listCTA {
        display: flex;
        height: fit-content;
      }
    }
    .list-edit-header{
      display: flex;
    }
    .list-edit-actions{
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-left: 14px;

      .listCTA {
        button:first-child{
          margin-left: 0 !important;
        }
      }
    }
  }
  @media screen and (max-width:560px){
    .cardRow  .shortContent {
      flex-direction: column;
      .listCTA {
        margin-top: 8px;
      }
      
      .editButtons {
        margin: 4px -20px 0;
        .border {
          border: none;
          flex: 1;
        }
        
 
      }
    }

  }

  .content {
    width: 100%;
  }

  .bottomSection {
    flex-grow: 1;
    align-self: flex-end;
    padding-top: 8px;

    button {
      min-width: auto;
    }
  }

  .padded {
    display: flex;
    flex-wrap: wrap;
    width:100%;
  }
  .titleLink {
    margin: 0 0 8px 0;
  }

  .cardTop {
    margin-bottom: 8px;
    display: flex;
    align-items: flex-start;
    gap: 16px;

    & > *:nth-child(2) {
      width:100%;
      margin-top: 16px;
      //border-left: 1px solid ${styleVariables.inkOnBackgroundDisabled};
      //padding-left: 0.75em;
    }

    .cardType {
      height: 20px;
      span {
        height: 20px;
        line-height: 20px;
      }
      & > *:nth-child(2) {
        border-left: 1px solid ${styleVariables.lightDivider};
        padding-left: 0.75em;
        margin-left: 0.75em;
      }
    }

  }

  .cardActions {
    display:flex;
    align-items: center;
    flex-flow: row nowrap;
    align-content: space-between;
    padding-bottom: 22px;
    margin-top: -3px;
    &>* {
      margin-top: 0px;
    }
    &>div {
      flex-basis: 85%;
      text-align: right;
    }
  }
`;

CommonCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    objectType: PropTypes.string,
    learningMode: PropTypes.string,
    effort: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.shape({})),
    itemCount: PropTypes.number,
    isStandAlone: PropTypes.bool,
  }).isRequired,
  learningRecords: PropTypes.shape({}),
  clickEvent: PropTypes.func,
  className: PropTypes.string,
  analyticsArea: PropTypes.string,
  setReturnUrl: PropTypes.func,
  state: PropTypes.string,
  cardRefs: PropTypes.shape({ current: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))]) }),
  toggleDelete: PropTypes.func,
  deletedListItems: PropTypes.arrayOf(PropTypes.string),
  isThemeDark: PropTypes.bool,
  canDrag: PropTypes.bool,
  itemIndex: PropTypes.number,
  showNumberedCircle: PropTypes.bool,
  isItemFromFastTrackLearningPlan: PropTypes.bool,
  // moveCard: PropTypes.func,
};

CommonCard.defaultProps = {
  learningRecords: {},
  clickEvent: ({
    data, analyticsArea, env, learningRecords, buttonName, isItemFromFastTrackLearningPlan,
  }) => {
    if (analyticsArea === 'learningplan') {
      triggerLearningPlanContentClick({
        data, learningRecords, env, isItemFromFastTrackLearningPlan,
      });
    } else {
      triggerManualLinkClick({ linkTitle: `${analyticsArea}:${buttonName}:${generateCourseDataAnalyticsString({ data, learningRecords, env })}` });
    }
  },
  setReturnUrl: () => {},
  state: 'default',
  cardRefs: { current: [] },
  className: '',
  analyticsArea: '',
  toggleDelete: undefined,
  deletedListItems: [],
  isThemeDark: false,
  canDrag: true,
  itemIndex: 0,
  showNumberedCircle: false,
  isItemFromFastTrackLearningPlan: false,
  // moveCard: undefined,
};

export default React.memo(CommonCard);
